import { Button } from 'antd';
import { MapPin, MapPinOff, ShieldAlert } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { ConnectionBadge } from '../../../shared/components/ConnectionBadge';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { useColorMode } from '../../../shared/features/theme/components/ColorModeProvider';
import { useZonesMapConfig } from '../../zones/public/use-zones-map-config';
import { useTripFocus } from '../hooks/use-trip-focus';
import { useTripMap } from '../hooks/use-trip-map';
import type { TripStop } from '../policies/trip-stops.policy';
import { useTripTrack, type TripTrackState } from '../hooks/use-trip-track';
import type { TripPageData } from '../trips.api';

const TRIP_OUT_STATUSES = ['dispatched', 'pre_trip_failed', 'in_transit', 'exception'];

interface TripMapPanelProps {
  trip: TripPageData;
  stops: TripStop[];
  onFreshFix: () => void;
}

function notice(tileKey: string | null | undefined, track: TripTrackState, retry: () => void): ReactNode {
  if (tileKey === null) {
    return <EmptyState icon={MapPinOff} title="Map key not configured" description="This deployment hasn't set a MapTiler key, so there is no map." />;
  }
  if (tileKey === undefined || track.status === 'loading') {
    return (
      <SkeletonRegion label="Loading map" className="h-full">
        <Skeleton className="h-full rounded-lg" />
      </SkeletonRegion>
    );
  }
  if (track.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load the track" description="Something went wrong reading this trip's positions.">
        <Button onClick={retry}>Try again</Button>
      </EmptyState>
    );
  }
  if (track.points.length === 0) {
    return <EmptyState icon={MapPin} title="No positions yet" description="The route appears once the driver's phone or the truck's tracker reports." />;
  }
  return null;
}

/** The trip's route from dispatch, the truck where it was last seen, and whether that is moving as it happens. */
export function TripMapPanel({ trip, stops, onFreshFix }: TripMapPanelProps) {
  const config = useZonesMapConfig();
  const { mode } = useColorMode();
  const tileKey = config === undefined ? undefined : config.tileKey;
  const { state, connection, retry } = useTripTrack({
    tripId: trip.id,
    truckId: trip.truck?.id ?? null,
    watchLive: TRIP_OUT_STATUSES.includes(trip.status),
    onFreshFix,
  });
  const points = useMemo(() => (state.status === 'loaded' ? state.points : []), [state]);
  const { focus, focusStop } = useTripFocus({ tripId: trip.id, points });
  const { containerRef } = useTripMap({ tileKey: tileKey ?? null, mode, points, stops, focus });
  const overlay = notice(tileKey, state, retry);

  return (
    <section aria-label="Trip map" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
        <span>{state.status === 'loaded' && state.isThinned ? 'Showing a sample of the positions' : 'Positions since dispatch'}</span>
        <ul aria-label="Map legend" className="flex items-center gap-3">
          <li className="flex items-center gap-1">
            <span aria-hidden className="inline-block h-0.5 w-5 bg-primary" /> Tracker (GPS)
          </li>
          <li className="flex items-center gap-1">
            <span aria-hidden className="inline-block w-5 border-t-2 border-dashed border-accent" /> Driver's phone
          </li>
        </ul>
        {connection && <ConnectionBadge connection={connection} />}
      </div>
      <div className="relative h-72 sm:h-96">
        <div ref={containerRef} className="h-full w-full rounded-lg border border-line" />
        {overlay && <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-surface/90">{overlay}</div>}
      </div>
      {stops.length > 0 && (
        <ol aria-label="Stops" className="flex flex-wrap gap-2 text-sm">
          {stops.map((stop) => (
            <li key={stop.position}>
              <Button size="small" disabled={stop.lat === null} onClick={() => focusStop(stop)}>
                {stop.name}
              </Button>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
